import isObject from "lodash/isObject";
import { OsType } from "@/constants/devices";
import type { DeviceVersionItem } from "@/_store/devices-settings.module";

const LAMBDA_URL = import.meta.env.VITE_AWS_LAMBDA_URL;

/**
 * Encodes a URI component, additionally escaping `!`, `'`, `(`, `)`, and `*` for strict RFC 3986 compliance.
 * @param str
 */
const fixedEncodeURIComponent = (str: string) =>
  encodeURIComponent(str).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16)}`);

/**
 * Builds a URL query string from an object, recursing into nested objects and prefixing with `?` when non-empty.
 * @param params
 */
const buildQuery = (params: object): string => {
  if (!isObject(params)) return "";

  let query: string = Object.keys(params as object)
    .map((key) => {
      const paramValue = params[key as keyof typeof params];
      if (typeof paramValue === "object") {
        return buildQuery(paramValue);
      }
      return `${key}=${fixedEncodeURIComponent(paramValue)}`;
    })
    .filter((p) => !!p)
    .join("&");

  if (query) {
    query = `?${query}`;
  }

  return query;
};

/**
 * Triggers a browser download for an in-memory Blob.
 * Creates a temporary object URL, clicks the link, then revokes the URL.
 */
export const downloadFile = (blob: Blob, fileName: string) => {
  if (!blob || !fileName) {
    console.warn("Can't download, blob or fileName is empty");
    return;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
  link.remove();
};

/**
 * Triggers a browser download by navigating a temporary anchor element to the given URL.
 */
export const downloadFileByLink = (downloadLink: string) => {
  const linkEl = document.createElement("a");
  linkEl.href = downloadLink;
  document.body.appendChild(linkEl);
  linkEl.click();
  document.body.removeChild(linkEl);
};

/**
 * Fetches a signed download URL from the AWS Lambda endpoint for a given agent version.
 */
export const getDownloadLink = async ({
  id,
  os,
  version,
}: {
  id: string;
  os?: string;
  version?: string;
}) => {
  const queryString = buildQuery({
    uuid: id,
    os,
    version,
    env: import.meta.env.VITE_CLIENT_ENV,
  });
  const response = await fetch(`${LAMBDA_URL}/${queryString}`);
  return await response.json();
};

/**
 * Maps a platform `OsType` to the AWS-expected OS key (`win`, `macos`, `linux`).
 */
export const getConvertedOSTypeForAWS = (
  osType: OsType.WINDOWS | OsType.OSX | OsType.LINUX
): "win" | "macos" | "linux" => {
  const osTypesMap = {
    [OsType.WINDOWS]: "win",
    [OsType.OSX]: "macos",
    [OsType.LINUX]: "linux",
  } as const;
  return osTypesMap[osType];
};

/**
 * Resolves the platform-specific download URL for a device version item.
 * @param item - Device version to download
 * @param downloadClientPageLink - URL containing the workspace UUID (used to extract agent ID)
 */
export async function createDownloadLink(
  item: DeviceVersionItem,
  downloadClientPageLink: string
): Promise<string> {
  const url: { [key in "win" | "macos" | "linux"]: string } = await getDownloadLink({
    id: downloadClientPageLink.split("/")[4],
    os: getConvertedOSTypeForAWS(item.osType),
    version: item.version,
  });
  const convertedOsType = getConvertedOSTypeForAWS(item.osType);
  return url[convertedOsType];
}

/**
 * Triggers a file download by appending a temporary anchor element and clicking it.
 * @param downloadLink - The download URL
 */
export async function handleDownloadAction(downloadLink: string): Promise<void> {
  const linkEl = document.createElement("a");
  linkEl.href = downloadLink;
  document.body.appendChild(linkEl);
  linkEl.click();
  document.body.removeChild(linkEl);
}

/**
 * Strips leading and trailing non-alphanumeric characters from each file type string.
 * Used to normalize raw file extension values (e.g. `.exe,` → `exe`).
 *
 * @param fileTypes - Array of raw file type strings to clean.
 * @returns Array of normalized file type strings.
 */
export const clearFileTypes = (fileTypes: string[]): string[] => {
  return fileTypes.map((type) => type.replace(/(^[^A-Z0-9]+)|([^A-Z0-9]+$)/gi, ""));
};
